import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_FORM = {
    modelo_id: '',
    serial_fisico: '',
    serial_olt: '',
    ip: '',
    estado: 'bodega',
    notas: ''
};

const ESTADO_COLOR = {
    bodega: 'bg-blue-100 text-blue-700',
    instalado: 'bg-green-100 text-green-700',
    pendiente_devolucion: 'bg-yellow-100 text-yellow-700',
    eliminado: 'bg-gray-100 text-gray-500'
};

const ESTADO_LABEL = {
    bodega: 'En Bodega',
    instalado: 'Instalado',
    pendiente_devolucion: 'Pendiente Devolución',
    eliminado: 'Eliminado'
};

const Equipos = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [modelos, setModelos] = useState([]);
    const [modelosFiltrados, setModelosFiltrados] = useState([]);
    const [stats, setStats] = useState({ total: 0, instalados: 0, bodega: 0, pendientes_devolucion: 0, eliminados: 0 });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [marcaSeleccionada, setMarcaSeleccionada] = useState('');
    const [editingUuid, setEditingUuid] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [eqRes, statsRes, marRes, modRes] = await Promise.all([
                api.get('/inventario/equipos'),
                api.get('/inventario/equipos/stats'),
                api.get('/inventario/marcas'),
                api.get('/inventario/modelos')
            ]);
            setItems(eqRes.data);
            setStats({
                total: eqRes.data.length,
                instalados: Number(statsRes.data.instalados) || 0,
                bodega: Number(statsRes.data.bodega) || 0,
                pendientes_devolucion: Number(statsRes.data.pendientes_devolucion) || 0,
                eliminados: Number(statsRes.data.eliminados) || 0
            });
            setMarcas(marRes.data);
            setModelos(modRes.data);
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    // Filtrar modelos cuando cambia la marca seleccionada en el modal
    useEffect(() => {
        if (marcaSeleccionada) {
            setModelosFiltrados(modelos.filter(m => String(m.marca_id) === String(marcaSeleccionada)));
        } else {
            setModelosFiltrados(modelos);
        }
        setForm(prev => ({ ...prev, modelo_id: '' }));
    }, [marcaSeleccionada, modelos]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setMarcaSeleccionada('');
        setEditingUuid(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        const marca = modelos.find(m => String(m.id) === String(item.modelo_id));
        setMarcaSeleccionada(marca ? String(marca.marca_id) : '');
        setForm({
            modelo_id: item.modelo_id,
            serial_fisico: item.serial_fisico || '',
            serial_olt: item.serial_olt || '',
            ip: item.ip || '',
            estado: item.estado || 'bodega',
            notas: item.notas || ''
        });
        setEditingUuid(item.uuid);
        setError('');
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editingUuid) {
                await api.put(`/inventario/equipos/${editingUuid}`, form);
                showSuccess('Equipo actualizado correctamente');
            } else {
                await api.post('/inventario/equipos', form);
                showSuccess('Equipo registrado en bodega correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando equipo');
        } finally {
            setSaving(false);
        }
    };

    const filtered = items.filter(i => {
        const matchSearch = `${i.marca_nombre} ${i.modelo_nombre} ${i.serial_fisico || ''} ${i.serial_olt || ''} ${i.ip || ''} ${i.cliente_nombre || ''}`.toLowerCase().includes(search.toLowerCase());
        const matchEstado = !filtroEstado || i.estado === filtroEstado;
        return matchSearch && matchEstado;
    });

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    const KPI = ({ label, value, color }) => (
        <div className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${color}`}>
            <p className="text-xs text-gray-500 uppercase font-medium">{label}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Equipos</h2>
                {can('inv_equipos', 'create') && (
                    <button onClick={openCreate} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Agregar Nuevo
                    </button>
                )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <KPI label="Total" value={stats.total} color="border-gray-400" />
                <KPI label="Instalados" value={stats.instalados} color="border-green-500" />
                <KPI label="En Bodega" value={stats.bodega} color="border-blue-500" />
                <KPI label="Pend. Devolución" value={stats.pendientes_devolucion} color="border-yellow-500" />
                <KPI label="Eliminados" value={stats.eliminados} color="border-gray-300" />
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="flex gap-3 mb-4">
                <input type="text" placeholder="Buscar por marca, modelo, serial, IP o cliente..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none">
                    <option value="">Todos los estados</option>
                    <option value="bodega">En Bodega</option>
                    <option value="instalado">Instalado</option>
                    <option value="pendiente_devolucion">Pendiente Devolución</option>
                    <option value="eliminado">Eliminado</option>
                </select>
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron equipos</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Equipo</th>
                                <th className="px-4 py-3 text-left">Serial Físico</th>
                                <th className="px-4 py-3 text-left">Serial OLT</th>
                                <th className="px-4 py-3 text-left">IP</th>
                                <th className="px-4 py-3 text-left">Cliente</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                {can('inv_equipos', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.uuid} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-gray-800">{item.marca_nombre} {item.modelo_nombre}</p>
                                        <p className="text-xs text-gray-400">{item.tecnologia}</p>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{item.serial_fisico || '—'}</td>
                                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{item.serial_olt || '—'}</td>
                                    <td className="px-4 py-3 text-gray-600">{item.ip || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.cliente_nombre || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLOR[item.estado]}`}>
                                            {ESTADO_LABEL[item.estado]}
                                        </span>
                                    </td>
                                    {can('inv_equipos', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingUuid ? 'Editar equipo' : 'Agregar equipo'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div>
                                <label className={labelClass}>Marca</label>
                                <select value={marcaSeleccionada} onChange={e => setMarcaSeleccionada(e.target.value)} className={inputClass} disabled={!!editingUuid}>
                                    <option value="">Seleccionar marca...</option>
                                    {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Modelo *</label>
                                <select name="modelo_id" value={form.modelo_id} onChange={handleChange} className={inputClass} disabled={!!editingUuid}>
                                    <option value="">{marcaSeleccionada ? 'Seleccionar modelo...' : 'Seleccione una marca primero'}</option>
                                    {modelosFiltrados.map(m => <option key={m.id} value={m.id}>{m.nombre} ({m.tecnologia})</option>)}
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Serial físico</label>
                                <input name="serial_fisico" value={form.serial_fisico} onChange={handleChange} className={inputClass} placeholder="Serial impreso en el equipo" />
                            </div>

                            <div>
                                <label className={labelClass}>Serial OLT</label>
                                <input name="serial_olt" value={form.serial_olt} onChange={handleChange} className={inputClass} placeholder="Serial largo para aprovisionar (ONT/CATV)" />
                            </div>

                            <div>
                                <label className={labelClass}>IP asignada</label>
                                <input name="ip" value={form.ip} onChange={handleChange} className={inputClass} placeholder="Ej: 192.168.30.100" />
                            </div>

                            {editingUuid && (
                                <div>
                                    <label className={labelClass}>Estado</label>
                                    <select name="estado" value={form.estado} onChange={handleChange} className={inputClass}>
                                        <option value="bodega">En Bodega</option>
                                        <option value="instalado">Instalado</option>
                                        <option value="pendiente_devolucion">Pendiente Devolución</option>
                                        <option value="eliminado">Eliminado</option>
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className={labelClass}>Notas</label>
                                <textarea name="notas" value={form.notas} onChange={handleChange} rows={2}
                                    className={inputClass} placeholder="Observaciones del equipo..." />
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">Cancelar</button>
                            <button onClick={handleSubmit} disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : (editingUuid ? 'Guardar cambios' : 'Agregar a bodega')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Equipos;
